<script lang="ts">
import validatorIsInt from 'validator/es/lib/isInt'
import isFloat from 'validator/es/lib/isFloat'

export function fixQty({ qty, stock, settings }: { qty: number; stock: number | None; settings: gen.ProductSettingsQtyFragment | None }): number {
  if (qty < 0) return 0
  if (helpers.isDef(stock) && qty > stock) return stock
  if (settings) {
    if (settings.min && qty < settings.min) return settings.min
    if (settings.max && qty > settings.max) return settings.max
    if (!settings.step) return Math.floor(qty)
    // snap qty down to the nearest multiple of step, rounding away float noise
    const steps = Math.floor(helpers.round(qty / settings.step, 6))
    return helpers.round(steps * settings.step, 6)
  }
  return qty
}
</script>

<script setup lang="ts">
const emit = defineEmits<{ focus: [] }>()

const props = defineProps<{
  settingsQty: gen.ProductSettingsQtyFragment | None
  value: number
  disabled: boolean
  stock: number | None
  valueSet: (i: number) => void
}>()

const elem = useTemplateRef('elem')

const base = computed(() => {
  const min = helpers.toPositive(props.settingsQty?.min, 1)
  const max = [999]
  if (props.settingsQty?.max) max.push(props.settingsQty?.max)
  if (props.stock) max.push(props.stock)
  const step = helpers.toPositive(props.settingsQty?.step, 1)
  return {
    max: Math.min(...max),
    fractions: step < 1,
    step,
    min,
  }
})

function isValid(string: string | number): string is string {
  if (!helpers.isNumeric(string)) return false
  const valAsString = string.toString()
  const { max, fractions, min } = base.value

  if (!fractions) {
    return validatorIsInt(valAsString, {
      max,
      min,
      allow_leading_zeroes: false,
    })
  }
  const [ten, tenths] = valAsString.split('.')
  const tenLen = ten?.length
  if (typeof tenLen === 'number' && tenLen > 3) return false

  const tenthsLen = tenths?.length
  if (typeof tenthsLen === 'number' && tenthsLen > 1) return false

  return isFloat(valAsString, { min, max })
}

function toQtyValue(valnumb: number): string {
  const val = valnumb.toString()
  return val === '' || isValid(val) ? val.toString() : fixQty({ qty: val, settings: props.settingsQty, stock: base.value.max }).toString()
}

const data = useShallowReactiveSync(() => {
  return { qty: toQtyValue(props.value) }
})

function blur() {
  elem.value?.blur?.()
}

const chars = ' 0123456789'
const charsFractions = chars + '.,'
function checkChar(e: KeyboardEvent) {
  const validChars = base.value.fractions ? charsFractions : chars
  if (!validChars.includes(e.key)) e.preventDefault()
}

function onBlur() {
  if (props.disabled) return
  const value = fixQty({ qty: helpers.toNumber(data.qty), settings: props.settingsQty, stock: base.value.max })
  if (value === props.value) data.qty = toQtyValue(value)
  props.valueSet(value)
}

defineExpose({ blur })
</script>

<template>
  <input
    ref="elem"
    v-model="data.qty"
    :min="base.min"
    :step="base.step"
    :class="$style.i"
    :disabled="disabled"
    :max="base.max"
    type="number"
    dir="ltr"
    autocapitalize="none"
    allowzero="false"
    spellcheck="false"
    @keypress="checkChar"
    @blur.passive="onBlur"
    @keyup.passive.enter="blur"
    @focus.passive="emit('focus')"
  />
</template>

<style module>
.i {
  composes: global(d_fs1);
  composes: global(m_fs11);
  composes: global(_fw-xl);
  composes: global(_h100p);
  composes: global(_w100p);
  composes: global(_ls-m);
  composes: global(_txt_ctr);
  composes: global(_bg0);

  word-spacing: 1.2px;

  padding: 0;
  outline: none;
  color: var(--blue2);
  appearance: textfield;
  box-sizing: border-box;
  outline: none;
  background-color: var(--bg);
}

.i:disabled {
  cursor: not-allowed;
  color: var(--black-4);
}
</style>
